import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Table,
  Input,
  Col,
  FormGroup,
  CardFooter,
  CardHeader,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import downloadData from '../../../assets/icons/downloaddata.png';
import redPasswordIcon from '../../../assets/icons/redpasswordicon.png';
import editIcon from '../../../assets/icons/editblueicon.png';
import { tableColumns } from '../constants';
import './UserManagement.scss';
import CreateUser from '../../../components/Admin/CreateUser/CreateUser';
import ResetPassword from '../../../components/Admin/ResetPassword/ResetPassword';
import ConfirmUserStatus from '../../../components/Admin/ConfirmUserStatus/ConfirmUserStatus';
import { useTableHook } from '../../../components/hooks';
import { fetchUsers, getByUserId } from '../../../store/apis/adminService';
import { fetchTableColumnValues } from '../../../store/apis/commonService';
import {
  FilterDropdown,
  TableFilterBubbles,
  TablePagination,
  TableSortDisplayArrow,
} from '../../../components/Common';
import { ExportUtil } from '../../../utils';
import EditUser from '../../../components/Admin/EditUser/EditUser';
import { Messages } from '../../../common/messages';

function UserManagement() {
  document.title = 'Admin - User Management | Daikin - Envision';

  const [selectedUser, setSelectedUser] = useState(null);
  const [addUserOC, setAddUserOC] = useState(false);
  const [resetPasswordOC, setResetPasswordOC] = useState(false);
  const [confirmStatusModal, setConfirmStatusModal] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editUserOC, setEditUserOC] = useState(false);

  const initialDefaultExternalFilters = {
    userName: '',
    isActive: 'true',
  };

  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    activeRows,
    handleRowCheck,
    selectAllRows,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    nextPage,
    prevPage,
    handleSetExternalFilters,
    applyExternalFilters,
    setPageLoadTrigger,
    tableDataMutation,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    refetch,
    isLoadingData,
    isLoadingColumn,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      ...initialDefaultExternalFilters,
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'Name',
      sortDirection: 'ASC',
      filters: [],
    },
    fetchFn: fetchUsers,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'Users',
    columnKeyPrefix: 'UsersColumn',
    tableName: 'Users',
    defaultExternalFilters: initialDefaultExternalFilters,
    onPageLoadTrigger: false,
  });

  useEffect(() => {
    setPageLoadTrigger(true);
    setPageRequest(prev => ({
      ...prev,
    }));
  }, [setPageRequest, setPageLoadTrigger]);

  // React Query with enabled: false
  const { data: fetchedUserData, refetch: refetchUserData } = useQuery({
    queryKey: ['getByUserId', editUserId],
    queryFn: () => getByUserId(editUserId),
    enabled: false,
  });

  // Trigger refetch when editUserId changes and modal is open
  useEffect(() => {
    if (editUserId && editUserOC) {
      refetchUserData();
    }

    if (editUserId && confirmStatusModal) {
      refetchUserData();
    }
  }, [editUserId, editUserOC, confirmStatusModal, refetchUserData]);

  const toggleConfirmStatusModal = () => {
    setConfirmStatusModal(!confirmStatusModal);
  };

  const toggleResetPasswordOC = () => {
    setResetPasswordOC(!resetPasswordOC);
    if (resetPasswordOC) setSelectedUser(null);
  };

  const toggleAddUserOC = () => {
    setAddUserOC(!addUserOC);
  };

  // Export based on filters without pagination
  const handleExportExcel = async () => {
    const exportData = data?.data;
    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);
    const exportResponseData = await tableDataMutation.mutateAsync({
      ...pageRequest,
      pageNumber: 1,
      pageSize: 0,
    });

    let excelData = exportResponseData?.data;
    if (activeRows?.length > 0) {
      excelData =
        exportResponseData?.data?.filter((_item, index) =>
          activeRows?.includes(index),
        ) || [];
    }
    ExportUtil.downloadExcel(excelData, {
      fileName: `Users_${timesTamp}.xlsx`,
      columns: {
        UserFullName: 'Employee Name',
        Email: 'Email',
        Name: 'Login ID',
        RoleName: 'Role Name',
        Status: 'Status',
      },
    });
  };

  const renderTableBody = () => {
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={6}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_USERS
            )}
          </td>
        </tr>
      );
    }

    return tableData?.map((user, index) => (
      <tr key={user.id}>
        <td className="py-2">
          <div className="form-check px-0">
            <Input
              className="form-check-input blue-checkbox"
              type="checkbox"
              name="chk_child"
              checked={activeRows?.includes(index)}
              onChange={e => handleRowCheck(index, e.target.checked)}
            />
          </div>
        </td>
        <td className="py-2">{user.UserFullName}</td>
        <td className="py-2">{user.Email}</td>
        <td className="py-2">{user.Name}</td>
        <td className="py-2">{user.RoleName}</td>
        <td className="py-2">
          <FormGroup switch className="d-flex align-items-center">
            <Input
              onClick={() => {
                toggleConfirmStatusModal();
                setEditUserId(user.ID);
              }}
              type="switch"
              name={`switch-${user.ID}`}
              id={`switch-${user.ID}`}
              checked={user.Status === 'Active'}
              readOnly
              className="me-2"
            />
            <label htmlFor="userStatusSwitch" className="fw-light mb-0">
              {user.Status === 'Active' ? 'Active' : 'Inactive'}
            </label>
          </FormGroup>
        </td>
        <td className="py-2">
          <div className="d-flex flex-row align-items-center justify-content-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => {
                setSelectedUser(user);
                setResetPasswordOC(true);
              }}
              title="Reset Password"
            >
              <img
                src={redPasswordIcon}
                alt="Reset Password"
                style={{ height: '15px' }}
              />
            </button>
            <button
              title="Edit User"
              type="button"
              className="btn btn-link p-0"
              onClick={() => {
                setEditUserId(user.ID);
                setEditUserOC(true);
              }}
            >
              <img src={editIcon} alt="Edit User" style={{ height: '15px' }} />
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="page-content user-management-page">
      <Card className="um-card">
        <CardHeader
          className="d-flex justify-content-between align-items-center pt-2 pb-2 border-0"
          style={{ borderRadius: '10px 10px 0 0' }}
        >
          <div className="user-management-title">
            <span className="fs-5" style={{ color: '#000', fontWeight: '600' }}>
              User Management
            </span>
            <div className="role-count">{data?.totalCount ?? 0}</div>
          </div>
          <div className="d-flex flex-row align-items-center gap-2">
            {/* Buttons */}
            <button
              type="button"
              className="um-btn-left"
              onClick={handleExportExcel}
              title="Download Data"
            >
              <img
                src={downloadData}
                alt="Download Data"
                style={{ height: '15px' }}
              />
            </button>
            <Button onClick={toggleAddUserOC} className="custom-btn">
              ADD
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="external-filter gap-2 mx-3 p-3">
            <Col className="external-filter-col">
              <b>Employee ID</b>
              <Input
                type="text"
                placeholder="Employee ID"
                className="form-control external-filter-input"
                value={externalFilters.userName}
                onChange={e =>
                  handleSetExternalFilters('userName', e.target.value)
                }
              />
            </Col>
            <Col className="external-filter-col">
              <b>Active Status</b>
              <Input
                type="select"
                className="form-select external-filter-input"
                value={externalFilters.isActive}
                onChange={e =>
                  handleSetExternalFilters('isActive', e.target.value)
                }
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Input>
            </Col>
            <Col>
              <div className="external-filter-btns gap-2">
                <button
                  type="button"
                  className="custom-btn"
                  onClick={() => applyExternalFilters(externalFilters)}
                >
                  Search
                </button>
              </div>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={tableColumns}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive users">
            <Table
              hover
              className="custom-table align-middle text-nowrap users-table"
            >
              <thead>
                <tr>
                  <th>
                    <div className="form-check px-0">
                      <Input
                        className="form-check-input blue-checkbox"
                        type="checkbox"
                        id="checkbox"
                        checked={
                          activeRows?.length > 0 &&
                          activeRows?.length === data?.data?.length
                        }
                        onChange={e => selectAllRows(e?.target?.checked)}
                      />
                    </div>
                  </th>
                  {Object.entries(tableColumns).map(([key, value]) => (
                    <th key={key} className="px-2">
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => handleAutoSort(key)}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2" style={{ borderRadius: '0 0 10px 10px' }}>
          <div>
            <TablePagination
              nextPage={nextPage}
              prevPage={prevPage}
              totalPages={data?.totalPages}
              currentPage={data?.currentPage}
              hasPreviousPage={data?.hasPreviousPage}
              hasNextPage={data?.hasNextPage}
              getPageSize={getPageSize}
              setPageSize={setPageSize}
              totalCount={data?.totalCount}
            />
          </div>
        </CardFooter>
      </Card>
      <CreateUser
        isOpen={addUserOC}
        toggle={toggleAddUserOC}
        onUserCreated={() => setPageLoadTrigger(true)}
      />
      <EditUser
        isOpen={editUserOC}
        toggle={() => {
          setEditUserOC(false);
        }}
        userData={fetchedUserData}
        onUserUpdated={() => {
          setPageLoadTrigger(true);
          setEditUserOC(false);
        }}
        refetch={refetch}
      />
      <ResetPassword
        isOpen={resetPasswordOC}
        toggle={toggleResetPasswordOC}
        user={selectedUser}
      />
      <ConfirmUserStatus
        isOpen={confirmStatusModal}
        toggle={toggleConfirmStatusModal}
        userData={fetchedUserData}
        onStatusUpdated={refetch}
      />
    </div>
  );
}

export default UserManagement;
